// Briques partagées (remplacent les composants UI de Faast) : un seul bouton,
// un seul menu, une seule notification, une seule confirmation pour tout
// Feedbaacks, widget et tableau de suivi compris.
import type { ComponentChildren, JSX } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import type { Mention } from '@feedbaacks/sdk'
import { useFeedbaacks } from './context'
import { Markdown } from './markdown'
import { Check, ChevronDown, Loader, X } from './icons'
import { useSelector } from './store'
import { cx } from './util'

export function Spinner({ size = 16 }: { size?: number }): JSX.Element {
  return <Loader size={size} class="fb-spin" />
}

export function Avatar({ name, src }: { name: string; src?: string }): JSX.Element {
  const initials = name.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?'
  return src ? <img class="fb-avatar" src={src} alt="" loading="lazy" /> : <span class="fb-avatar" aria-hidden="true">{initials}</span>
}

/** Texte d'utilisateur : Markdown sûr (sauts de ligne gardés, liens
 *  cliquables, mentions en pastille), jamais de HTML interprété. */
export function Prose({ text, mentions, class: cls }: { text: string; mentions?: Mention[]; class?: string }): JSX.Element {
  return (
    <div class={cx('fb-prose', cls)}>
      <Markdown text={text} mentions={mentions} />
    </div>
  )
}

export function IconButton({
  label, onClick, children, active, danger, testId, disabled,
}: {
  label: string
  onClick: (e: MouseEvent) => void
  children: ComponentChildren
  active?: boolean
  danger?: boolean
  testId?: string
  disabled?: boolean
}): JSX.Element {
  return (
    <button
      type="button"
      class={cx('fb-icon-btn', active && 'is-active', danger && 'is-danger')}
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation()
        onClick(e)
      }}
      data-testid={testId}
      disabled={disabled}
    >
      {children}
    </button>
  )
}

export function Button({
  children, onClick, variant = 'secondary', type = 'button', disabled, testId,
}: {
  children: ComponentChildren
  onClick?: () => void
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  type?: 'button' | 'submit'
  disabled?: boolean
  testId?: string
}): JSX.Element {
  return (
    <button type={type} class={cx('fb-btn', `fb-btn-${variant}`)} onClick={onClick} disabled={disabled} data-testid={testId}>
      {children}
    </button>
  )
}

export interface ChipOption<T extends string> {
  value: T
  label: string
  count?: number
}

/** Rangée de puces à choix unique (filtres). */
export function ChipRow<T extends string>({
  label, options, value, onChange,
}: {
  label: string
  options: Array<ChipOption<T>>
  value: T
  onChange: (v: T) => void
}): JSX.Element {
  return (
    <div role="radiogroup" aria-label={label} class="fb-chips">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} class={cx('fb-chip', o.value === value && 'is-active')} onClick={() => onChange(o.value)}>
          <span>{o.label}</span>
          {o.count != null && <span class={cx('fb-chip-count', o.count === 0 && 'is-zero')}>{o.count}</span>}
        </button>
      ))}
    </div>
  )
}

/** Bascule en forme de puce. */
export function ToggleChip({
  label, checked, onChange, count, testId,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
  count?: number
  testId?: string
}): JSX.Element {
  return (
    <button type="button" role="switch" aria-checked={checked} class={cx('fb-chip', checked && 'is-active')} onClick={() => onChange(!checked)} data-testid={testId}>
      <span>{label}</span>
      {count != null && <span class="fb-chip-count">{count}</span>}
    </button>
  )
}

/** Ferme au clic extérieur et à Échap (composé : marche dans un Shadow DOM). */
export function useDismiss(open: boolean, close: () => void, ref: { current: HTMLElement | null }): void {
  useEffect(() => {
    if (!open) return
    const onDown = (e: Event) => {
      if (ref.current && !e.composedPath().includes(ref.current)) close()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        close()
      }
    }
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [open, close, ref])
}

/** Menu déroulant : un déclencheur, des options, l'option courante cochée. */
export function Menu<T extends string>({
  value, options, onChange, renderTrigger, label, renderOption, testId,
}: {
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (v: T) => void
  renderTrigger: (open: boolean) => ComponentChildren
  renderOption?: (o: { value: T; label: string }, active: boolean) => ComponentChildren
  label: string
  testId?: string
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(open, () => setOpen(false), ref)
  return (
    <div class="fb-menu" ref={ref}>
      <button type="button" class="fb-menu-trigger" aria-haspopup="listbox" aria-expanded={open} aria-label={label} data-testid={testId}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}>
        {renderTrigger(open)}
      </button>
      {open && (
        <ul class="fb-menu-list" role="listbox" aria-label={label}>
          {options.map((o) => (
            <li key={o.value}>
              <button type="button" role="option" aria-selected={o.value === value} class="fb-menu-item"
                onClick={(e) => {
                  e.stopPropagation()
                  setOpen(false)
                  if (o.value !== value) onChange(o.value)
                }}>
                {renderOption ? renderOption(o, o.value === value) : (
                  <>
                    <span class="fb-menu-check">{o.value === value && <Check size={12} />}</span>
                    {o.label}
                  </>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function Disclosure({ title, icon, children, defaultOpen = false }: { title: string; icon?: ComponentChildren; children: ComponentChildren; defaultOpen?: boolean }): JSX.Element {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <section class="fb-disclosure">
      <button type="button" class="fb-disclosure-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {icon}
        <span>{title}</span>
        <ChevronDown size={12} class={cx('fb-chevron', open && 'is-open')} />
      </button>
      {open && <div class="fb-disclosure-body">{children}</div>}
    </section>
  )
}

/** Hôte des notifications et de la confirmation : monté une fois par instance. */
export function Overlays(): JSX.Element {
  const { toasts, t } = useFeedbaacks()
  const list = useSelector(toasts, (s) => s.list)
  const confirm = useSelector(toasts, (s) => s.confirm)
  const close = (ok: boolean) => {
    confirm?.resolve(ok)
    toasts.set({ confirm: null })
  }
  return (
    <>
      <div class="fb-toasts" role="status" aria-live="polite">
        {list.map((n) => (
          <div key={n.id} class={cx('fb-toast', `is-${n.kind}`)} data-testid="feedbaacks-toast">
            {n.message}
            <button type="button" class="fb-toast-close" aria-label={t('close')} onClick={() => toasts.set((s) => ({ list: s.list.filter((x) => x.id !== n.id) }))}>
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
      {confirm && (
        <div class="fb-confirm-backdrop" onClick={() => close(false)}>
          <div class="fb-confirm" role="alertdialog" aria-modal="true" aria-label={confirm.title} onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.key === 'Escape' && close(false)}>
            <h3>{confirm.title}</h3>
            <p>{confirm.message}</p>
            <div class="fb-confirm-actions">
              <Button variant="ghost" onClick={() => close(false)}>{confirm.cancelLabel ?? t('cancel')}</Button>
              <Button variant={confirm.destructive ? 'danger' : 'primary'} onClick={() => close(true)} testId="feedbaacks-confirm">
                {confirm.confirmLabel ?? t('confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
